import React, { useState } from 'react';
import { FileText, Image, Folder, Eye } from 'lucide-react';
import { Asset } from './FileExplorer';

interface FileGridProps {
  assets: Asset[];
  selectedAssets: Asset[];
  onAssetSelect: (asset: Asset) => void;
  onViewDetails: (asset: Asset) => void;
  allowedFileTypes?: string[];
  isPicker?: boolean;
  multiSelect?: boolean;
  onNavigate: (folder: string) => void;
}

export const FileGrid: React.FC<FileGridProps> = ({
  assets,
  selectedAssets,
  onAssetSelect,
  onViewDetails,
  allowedFileTypes,
  isPicker = false,
  multiSelect = true,
  onNavigate,
}) => {
  const [hoveredFile, setHoveredFile] = useState<string | null>(null);

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image className="w-12 h-12 text-blue-500" />;
    if (mimeType === 'folder') return <Folder className="w-12 h-12 text-yellow-500" />;
    return <FileText className="w-12 h-12 text-gray-500" />;
  };

  const isSelectable = (asset: Asset) => {
    if (asset.mimeType === 'folder') return true; // Folders are always selectable
    if (!allowedFileTypes || allowedFileTypes.length === 0) return true;
    return allowedFileTypes.includes(asset.mimeType);
  };

  const handleItemClick = (asset: Asset, e: React.MouseEvent) => {
    if (asset.mimeType === 'folder') {
      onNavigate(asset.originalFile);
    } else if (isSelectable(asset) && !(e.target as HTMLElement).closest('.view-details-button')) {
      onAssetSelect(asset);
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
      {assets.map((asset) => {
        const isSelected = selectedAssets.some((a) => a.id === asset.id);
        const selectable = isSelectable(asset);
        return (
          <div
            key={asset.originalFile}
            className={`relative group bg-white p-4 sm:p-6 rounded-xl hover:transition duration-300 ease-in-out ${
              selectable ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
            } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
            onMouseEnter={() => setHoveredFile(asset.id || '')}
            onMouseLeave={() => setHoveredFile(null)}
            onClick={(e) => handleItemClick(asset, e)}
          >
            {multiSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => selectable && onAssetSelect(asset)}
                className="absolute top-2 left-2 z-0 pointer-events-none"
                disabled={!selectable}
              />
            )}
            <div className="flex flex-col items-center">
              <div className="flex justify-center mb-2">
                {asset.mimeType.startsWith('image/') ? (
                  <img src={asset.thumnailImage} alt={asset.filename} className="w-24 h-24 object-cover rounded" />
                ) : (
                  getFileIcon(asset.mimeType)
                )}
              </div>
              <p className="text-sm font-medium text-gray-800 truncate w-full text-center">{asset.filename}</p>
              <p className="text-xs text-gray-500">{(asset.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
            
            {!isPicker && hoveredFile === asset.id && (
              <div className="absolute bottom-0 left-0 right-0 bg-gray-100 bg-opacity-90 p-2 rounded-b-lg transition-all duration-200 transform translate-y-0">
                <button
                  className="view-details-button w-full flex items-center justify-center text-sm font-medium text-blue-600 hover:text-blue-800 transition duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(asset);
                  }}
                >
                  <Eye className="w-4 h-4 mr-1" /> View Details
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
